import { useRef, useState } from 'react'
import type { Shot, JobStatus, MergeJobStatus, TransitionConfig, TransitionType } from '../types'
import { TRANSITION_LABELS } from '../types'
import { FoundryPicker } from '../../foundry/components/FoundryPicker'

interface Props {
  group: string
  shots: Shot[]
  jobStatus: Record<string, JobStatus>
  projectId: string
  mergeJob: MergeJobStatus | null
  onMerge: (transitions: TransitionConfig[]) => void
  onClear: () => void
}

const TRANSITION_TYPES: TransitionType[] = [
  'dissolve', 'fade', 'fadewhite',
  'wipeleft', 'wiperight',
  'slideleft', 'slideright',
  'circleopen', 'pixelize',
]

const DURATION_PRESETS = [0.2, 0.3, 0.5, 0.75, 1.0, 1.5, 2.0]

const DEFAULT_TRANSITION: TransitionConfig = { type: 'dissolve', duration: 0.5 }

function defaultTransitions(count: number): TransitionConfig[] {
  return Array.from({ length: count }, () => ({ ...DEFAULT_TRANSITION }))
}

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="w-full bg-forge-slate rounded-full h-1.5 overflow-hidden">
      <div
        className="h-full bg-forge-ember transition-all duration-300 rounded-full"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

export function MergePanel({ group, shots, jobStatus, projectId, mergeJob, onMerge, onClear }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const ordered = [...shots].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))

  const [transitions, setTransitions] = useState<TransitionConfig[]>(() =>
    defaultTransitions(Math.max(0, ordered.length - 1))
  )
  const [showAssets, setShowAssets] = useState(false)
  const [charAssetIds, setCharAssetIds] = useState<string[]>([])
  const [sceneAssetIds, setSceneAssetIds] = useState<string[]>([])

  const state = mergeJob?.state ?? 'idle'
  const isActive = state === 'loading' || state === 'merging' || state === 'uploading'

  // Determine per-shot readiness
  const shotReadiness = ordered.map((s) => {
    const key = `${projectId}/${s.videoId}/${s.shotId}`
    return { shot: s, done: jobStatus[key]?.state === 'done', key }
  })
  const allReady = shotReadiness.every((r) => r.done)
  const readyCount = shotReadiness.filter((r) => r.done).length

  const setTransition = (idx: number, patch: Partial<TransitionConfig>) => {
    setTransitions((prev) => prev.map((t, i) => i === idx ? { ...t, ...patch } : t))
  }

  const borderColor =
    isActive ? 'border-forge-gold' :
    state === 'done' ? 'border-forge-green' :
    state === 'failed' ? 'border-forge-heat-critical' :
    'border-forge-ember/30'

  const phaseLabel =
    state === 'loading' ? 'Loading engine…' :
    state === 'merging' ? 'Encoding…' :
    state === 'uploading' ? 'Uploading…' : ''

  return (
    <div className={`mb-5 border ${borderColor} rounded-xl bg-forge-obsidian/50 overflow-hidden transition-colors duration-200`}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="w-6 h-6 rounded bg-forge-ember/10 flex items-center justify-center flex-shrink-0">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-forge-ember">
            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-[12px] font-bold text-forge-ember uppercase tracking-wider">
            Merge: {group}
          </h3>
          <p className="text-[11px] text-forge-ash">
            {ordered.length} shots · {readyCount}/{ordered.length} generated
            {allReady && <span className="text-forge-green ml-1">· ready to merge</span>}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {state === 'done' && (
            <button
              onClick={onClear}
              className="text-[11px] font-semibold rounded-md px-3 py-1.5 border border-forge-border text-forge-ash hover:text-forge-heat-critical hover:border-forge-heat-critical/40 transition-colors"
            >
              Clear
            </button>
          )}
          <button
            onClick={() => onMerge(transitions)}
            disabled={isActive || !allReady}
            className={`text-[11px] font-semibold rounded-md px-4 py-1.5 transition-all ${
              isActive || !allReady
                ? 'bg-forge-slate text-forge-cool-smoke cursor-not-allowed'
                : 'bg-forge-ember text-white hover:bg-forge-ember/80'
            }`}
          >
            {isActive
              ? phaseLabel
              : state === 'done'
                ? 'Re-Merge'
                : state === 'failed'
                  ? 'Retry Merge'
                  : 'Merge Sequence'}
          </button>
        </div>
      </div>

      {/* ── Foundry assets ─────────────────────────────────────────────────── */}
      {!isActive && (
        <div className="px-4 pb-2">
          <button
            onClick={() => setShowAssets(!showAssets)}
            className="text-[10px] font-semibold text-forge-ember hover:text-forge-ember/80 transition-colors"
          >
            {showAssets ? '− Hide Reference Assets' : '+ Reference Assets'}
            {!showAssets && (charAssetIds.length + sceneAssetIds.length > 0) &&
              ` (${charAssetIds.length + sceneAssetIds.length} selected)`}
          </button>
          {showAssets && (
            <div className="mt-2 space-y-2">
              <div>
                <span className="text-[9px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Character</span>
                <FoundryPicker type="character" selectedIds={charAssetIds} maxSelections={2} onSelectionChange={setCharAssetIds} />
              </div>
              <div>
                <span className="text-[9px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Scene</span>
                <FoundryPicker type="scene" selectedIds={sceneAssetIds} maxSelections={2} onSelectionChange={setSceneAssetIds} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Shot sequence + transition selectors ───────────────────────────── */}
      {!isActive && state !== 'done' && (
        <div className="px-4 pb-4">
          <div className="space-y-0">
            {ordered.map((s, i) => {
              const { done } = shotReadiness[i]
              return (
                <div key={s.shotId}>
                  {/* Shot pill */}
                  <div className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${
                    done ? 'border-forge-green/30 bg-forge-green/5' : 'border-forge-border/50 bg-forge-obsidian/30'
                  }`}>
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${
                      done ? 'bg-forge-green/20 text-forge-green' : 'bg-forge-slate text-forge-cool-smoke'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="text-[11px] text-forge-primary flex-1 truncate">{s.label}</span>
                    <span className={`text-[9px] font-semibold uppercase tracking-wider ${
                      done ? 'text-forge-green' : 'text-forge-cool-smoke'
                    }`}>
                      {done ? '✓ ready' : 'pending'}
                    </span>
                  </div>

                  {/* Transition selector between shots */}
                  {i < ordered.length - 1 && (
                    <div className="flex items-center gap-2 py-1.5 pl-6">
                      <div className="w-px h-4 bg-forge-border/50 flex-shrink-0" />
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-forge-ash flex-shrink-0">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                      <select
                        value={transitions[i]?.type ?? 'dissolve'}
                        onChange={(e) => setTransition(i, { type: e.target.value as TransitionType })}
                        className="bg-forge-obsidian border border-forge-border/60 rounded px-2 py-0.5 text-[10px] text-forge-primary focus:outline-none focus:border-forge-ember/50 transition-colors"
                      >
                        {TRANSITION_TYPES.map((t) => (
                          <option key={t} value={t}>{TRANSITION_LABELS[t]}</option>
                        ))}
                      </select>
                      <select
                        value={transitions[i]?.duration ?? 0.5}
                        onChange={(e) => setTransition(i, { duration: parseFloat(e.target.value) })}
                        className="bg-forge-obsidian border border-forge-border/60 rounded px-2 py-0.5 text-[10px] text-forge-primary focus:outline-none focus:border-forge-ember/50 transition-colors w-[60px]"
                      >
                        {DURATION_PRESETS.map((d) => (
                          <option key={d} value={d}>{d.toFixed(1)}s</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── Live progress ──────────────────────────────────────────────────── */}
      {isActive && mergeJob && (
        <div className="px-4 pb-4">
          <div className="rounded-lg bg-forge-obsidian border border-forge-gold/20 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-forge-gold">{phaseLabel}</span>
              <span className="text-[11px] font-mono text-forge-ash tabular-nums">
                {mergeJob.progress}%
              </span>
            </div>
            <ProgressBar pct={mergeJob.progress} />
            {mergeJob.log && (
              <p className="text-[10px] text-forge-cool-smoke font-mono truncate">{mergeJob.log}</p>
            )}
            <p className="text-[10px] text-forge-cool-smoke">
              Processing happens in your browser. Do not close this tab.
            </p>
          </div>
        </div>
      )}

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {state === 'failed' && mergeJob?.error && (
        <div className="px-4 pb-3">
          <p className="text-[11px] text-forge-heat-critical leading-relaxed">{mergeJob.error}</p>
        </div>
      )}

      {/* ── Merged video player ────────────────────────────────────────────── */}
      {state === 'done' && mergeJob?.file && (
        <div className="px-4 pb-4 space-y-2">
          <video
            ref={videoRef}
            src={mergeJob.file}
            controls
            preload="metadata"
            className="w-full rounded-lg bg-black"
          />
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-forge-green font-semibold flex-1">
              ✓ Merged — {ordered.length} shots · saved to storage
            </span>
            <a
              href={mergeJob.file}
              download={`${group}-merged.mp4`}
              className="text-[11px] font-semibold text-forge-ash hover:text-forge-primary border border-forge-border rounded-md px-3 py-1 transition-colors"
            >
              Download
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
